import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '../../components/Box';
import WindRoseChart, { ROSE_COLORS } from '../../components/charts/WindRoseChart';
import WindTimelineChart from '../../components/charts/WindTimelineChart';
import TempDewPointChart from '../../components/charts/TempDewPointChart';
import PressureChart from '../../components/charts/PressureChart';
import TimeRangeToggle from '../../components/charts/TimeRangeToggle';
import ZoomControl from '../../components/charts/ZoomControl';
import CustomRangePicker from '../../components/charts/CustomRangePicker';
import HistoricalRangeBanner from '../../components/charts/HistoricalRangeBanner';
import useSharedZoom from '../../components/charts/useSharedZoom';
import { installPinchDeadZone } from '../../components/charts/zooming';
import Highcharts from '../../lib/charts/highcharts';
import { ThemeContext } from '../../contexts/ThemeContext';
import { getReadings } from '../../services/api';
import { applyChartTheme } from '../../lib/charts/theme';
import { normalizeRows, prepareTimeline, roseCells, parseSectors, dominantSectors, presentBins, ROSE_WINDOW_MIN } from '../../lib/charts/history';
import { parseRangeFromUrl, rangeToUrlParams, resolveWindow, formatRangeLabel } from '../../lib/charts/rangePresets';
import { formatMonthYear } from '../../lib/charts/time';
import { CHART_FEATURES } from '../../lib/charts/features';
import { nightBands } from '../../lib/charts/sun';
import { yesterday } from '../../lib/charts/rangeShortcuts';

// Mobile pinch: treat near-parallel two-finger moves as a pan (see zooming.js). Idempotent.
installPinchDeadZone(Highcharts);

const CACHE_MAX = 8;
// Shared across instances so a remount (e.g. crossing the md breakpoint) is a cache hit.
const readingsCache = new Map();
export const clearReadingsCache = () => readingsCache.clear();
// Presets anchor to the minute so re-anchoring within the same minute reuses the cache key.
const nowMinute = () => Math.floor(Date.now() / 60000) * 60000;

// Mobile: the History toolbar sticks just below the mobile tab bar (sticky top-28 = 112 px + its ~49 px).
const MOBILE_TOOLBAR_TOP = 159;

/**
 * Owns the dashboard charts: range state (URL-synced), the readings fetch
 * (aborted on change, small session cache), data prep, theme application
 * and the card layout. Mounted exactly once by StationDashboard.
 *
 * Desktop (default): Wind card (rose beside the timeline from xl, stacked
 * below), then full-width temperature and pressure cards.
 * Mobile (`compact`): one History card — full rose, then three stacked charts
 * with a sticky range/zoom toolbar; pinch on any chart is mirrored to the others.
 */
const ChartsSection = ({ stationId, station, allData, compact = false, historySince, refreshTick }) => {
    const { t, i18n } = useTranslation();
    const theme = useContext(ThemeContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const range = useMemo(() => parseRangeFromUrl(searchParams), [searchParams]);
    // Presets anchor to "now" when selected or when the station changes — not on every poll.
    const [anchorNow, setAnchorNow] = useState(nowMinute);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [state, setState] = useState({ rows: [], loading: true, error: null });
    // Desktop: each card's All/1h/3h control is portalled into its Box header (callback refs → state so the portal re-renders once mounted).
    const [windZoomEl, setWindZoomEl] = useState(null);
    const [tempZoomEl, setTempZoomEl] = useState(null);
    const [pressureZoomEl, setPressureZoomEl] = useState(null);
    const abortRef = useRef(null);
    const anchoredStation = useRef(stationId);
    // Set before a background re-anchor so load() keeps the old rows on screen (no spinner, no flicker).
    const silentRef = useRef(false);

    // Re-anchor presets to "now" only when the station actually changes (not on mount).
    useEffect(() => {
        if (anchoredStation.current === stationId) return;
        anchoredStation.current = stationId;
        setAnchorNow(nowMinute());
    }, [stationId]);
    // Live updates: the dashboard bumps `refreshTick` on every poll / manual refresh / tab return.
    // Presets re-anchor to "now" (→ a silent refetch, cache-keyed per minute); custom ranges are history and stay put.
    useEffect(() => {
        if (refreshTick === undefined || refreshTick === null || range.range === 'custom') return;
        const next = nowMinute();
        if (next === anchorNow) return; // same minute → nothing to refetch (and don't leave `silent` armed)
        silentRef.current = true;
        setAnchorNow(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTick, range.range]);
    // Child effects run before the ThemeProvider's effect toggles `.dark` on <html>;
    // defer a frame so the CSS variables read the NEW theme.
    useEffect(() => {
        const id = requestAnimationFrame(() => applyChartTheme());
        return () => cancelAnimationFrame(id);
    }, [theme?.theme]);

    const { fromMs, toMs } = useMemo(() => resolveWindow(range, anchorNow), [range, anchorNow]);

    const load = useCallback(() => {
        if (stationId === undefined || stationId === null) return;
        const key = `${stationId}|${fromMs}|${toMs}`;
        const cached = readingsCache.get(key);
        const silent = silentRef.current;
        silentRef.current = false;
        if (cached) { setState({ rows: cached, loading: false, error: null }); return; }
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setState((s) => ({ ...s, loading: silent && s.rows.length > 0 ? s.loading : true, error: null }));
        getReadings(stationId, fromMs, toMs, ctrl.signal)
            .then((rows) => {
                if (ctrl.signal.aborted) return;
                readingsCache.set(key, rows);
                if (readingsCache.size > CACHE_MAX) readingsCache.delete(readingsCache.keys().next().value);
                setState({ rows, loading: false, error: null });
            })
            .catch((err) => {
                if (ctrl.signal.aborted) return;
                // A failed background refresh keeps the last good data on screen instead of an error overlay.
                setState((s) => ({ ...s, loading: false, error: silent && s.rows.length > 0 ? null : err }));
            });
    }, [stationId, fromMs, toMs]);

    useEffect(() => { load(); return () => { if (abortRef.current) abortRef.current.abort(); }; }, [load]);

    const setRange = (next) => {
        silentRef.current = false; // a user action shows the spinner as usual
        setAnchorNow(nowMinute());
        // react-router 6.3: no functional updater — build from the current params.
        const p = new URLSearchParams(searchParams);
        ['range', 'from', 'to'].forEach((k) => p.delete(k));
        Object.entries(rangeToUrlParams(next)).forEach(([k, v]) => p.set(k, v));
        setSearchParams(p, { replace: true });
    };

    const onToggle = (key) => {
        if (key === 'custom') { setPickerOpen(true); return; }
        setPickerOpen(false);
        setRange({ range: key });
    };

    const points = useMemo(() => prepareTimeline(normalizeRows(state.rows), toMs - fromMs), [state.rows, fromMs, toMs]);
    const rose = useMemo(() => {
        const cutoff = Date.now() - ROSE_WINDOW_MIN * 60e3;
        // Presets end at "now", so the fetched readings cover the rose window and refresh with the timeline.
        // Custom (historic) ranges — and the moment before the first readings arrive — use the dashboard feed.
        const source = range.range !== 'custom' && state.rows.length > 0 ? state.rows : (allData || []);
        return roseCells(normalizeRows(source).filter((p) => p.t >= cutoff));
    }, [allData, state.rows, range.range]);
    const sectors = useMemo(() => parseSectors(station), [station]);
    const hasTemp = useMemo(() => points.some((p) => p.temp !== null || p.dewPoint !== null), [points]);
    const hasPressure = useMemo(() => points.some((p) => p.pressure !== null), [points]);

    // Night shading (sunset → sunrise from the station's coordinates) behind all three charts. Off switch: CHART_FEATURES.nightShading.
    const nightPlotBands = useMemo(() => {
        if (!CHART_FEATURES.nightShading) return [];
        return nightBands(fromMs, toMs, station && station.LAT, station && station.LONG).map((b) => ({ ...b, color: 'var(--chart-night)', zIndex: 0 }));
    }, [fromMs, toMs, station]);

    // Zoom resets only when the range/station changes — a background refresh keeps the user's 1h/3h preset.
    const zoomResetKey = `${stationId}|${range.range}|${range.fromMs || ''}|${range.toMs || ''}`;
    // Mobile: one zoom state for the three stacked charts (presets + mirrored pinch).
    // Phones open on the last 3 h (of the 8 h fetched) — a full 8 h is unreadable on a 350 px wide chart; All is one tap away.
    const sharedZoom = useSharedZoom(3, zoomResetKey, points, '3h');

    const zoomLabels = useMemo(() => ({ zoom: t('Zum'), all: t('Sve'), '1h': t('1h'), '3h': t('3h') }), [t]);
    const common = {
        loading: state.loading, error: state.error,
        emptyMessage: t('Nema očitavanja u ovom periodu'),
        errorMessage: t('Greška pri učitavanju istorije'),
        retryLabel: t('Pokušaj ponovo'), onRetry: load,
        zoomLabels, plotBands: nightPlotBands, zoomResetKey,
    };
    const toggleLabels = { '8h': t('8h'), '24h': t('24h'), '3d': t('3d'), custom: t('Prilagođeno…') };
    // "Archive: Jun 2019 – today" — how far back this station's readings go (constants.historySince, probed). Range phrasing avoids Serbian genitive.
    const archiveMonth = formatMonthYear(historySince, (i18n && i18n.language) || 'en');
    const archiveNote = archiveMonth ? `${t('Arhiva')}: ${archiveMonth} – ${t('danas')}` : null;
    // Rendered a step smaller and more muted than the subtitle itself (Milan: "subtle", "even smaller").
    const subtitleWithArchive = (
        <>
            {t('Vetar · temperatura · pritisak')}
            {archiveNote ? <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">– {archiveNote}</span> : null}
        </>
    );
    const customRangeText = range.range === 'custom' ? formatRangeLabel(range.fromMs, range.toMs) : undefined;
    // The toggle just says "Custom" while active; the dates live in the status line below.
    const customLabel = range.range === 'custom' ? t('Prilagođeno') : undefined;
    const binLabel = useCallback((b) => `${b} m/s`, []);
    const roseTooltip = useCallback((a) => `${a.direction} · ${a.bin} m/s · ${a.percentage.toFixed(0)}% (${a.count}/${a.total})`, []);
    const windLabels = useMemo(() => ({ wind: t('Brzina vetra'), gust: t('Udari vetra'), direction: t('Smer vetra'), safeBand: t('Idealno za poletanje') }), [t]);
    const tempLabels = useMemo(() => ({ temperature: t('Temperatura'), dewPoint: t('Tačka rosišta'), cloudBase: t('Baza oblaka') }), [t]);
    const pressureLabels = useMemo(() => ({ pressure: t('Vazdušni pritisak') }), [t]);

    const picker = (
        <CustomRangePicker open={pickerOpen} nowMs={Date.now()}
                           initialFromMs={range.range === 'custom' ? range.fromMs : yesterday(Date.now()).fromMs}
                           initialToMs={range.range === 'custom' ? range.toMs : yesterday(Date.now()).toMs}
                           onApply={(f, tt) => { setPickerOpen(false); setRange({ range: 'custom', fromMs: f, toMs: tt }); }}
                           onCancel={() => setPickerOpen(false)}
                           compact={compact} historySince={historySince} locale={(i18n && i18n.language) || 'en'}
                           labels={{ title: t('Prilagođen period'), close: t('Close'), hint: t('Najviše 5 dana'), archive: archiveNote, hours: t('Sati'), wholeDay: t('Ceo dan'), flyingHours: t('Sati letenja'), from: t('Od'), to: t('Do'), apply: t('Primeni'), cancel: t('Otkaži'),
                               errors: { fromAfterTo: t('Početak mora biti pre kraja'), spanTooLarge: t('Najviše 5 dana'), inFuture: t('Kraj ne može biti u budućnosti'), noRange: t('Izaberite period') },
                               shortcuts: { sameDayLastYear: t('Isti dan prošle godine'), yesterday: t('Juče'), lastWeekend: t('Prošli vikend'), thisDayLastWeek: t('Pre nedelju dana') } }} />
    );
    const rangeToggle = <TimeRangeToggle value={range.range} onChange={onToggle} labels={toggleLabels} customLabel={customLabel} />;
    const banner = range.range === 'custom' ? (
        <HistoricalRangeBanner text={`${t('Prikazan period')}: ${customRangeText}`} clearLabel={t('Vrati na 8h')} onClear={() => setRange({ range: '8h' })} />
    ) : null;

    // ------------------------------------------------------------------ mobile
    if (compact) {
        const dominant = dominantSectors(rose.cells, 2);
        const bins = presentBins(rose.cells, 3);
        // Section labels: generous top margin separates the stacked charts; a little bottom room keeps the label off the axis title.
        const sectionLabel = (text) => <div className="mt-8 mb-3 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">{text}</div>;
        return (
            // pb-24 keeps the shared navigator clear of the floating Forecast button.
            <div className="p-2 pb-24">
                <Box sizes="w-full" uppercase title={t('Istorija')} subtitle={subtitleWithArchive}>
                    <div className="sticky z-20 -mx-3 px-3 pt-2 pb-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-[0_6px_12px_-8px_rgba(0,0,0,0.35)] flex items-center justify-between gap-2 flex-wrap" style={{ top: MOBILE_TOOLBAR_TOP }}>
                        {rangeToggle}
                        <ZoomControl value={sharedZoom.zoom} onChange={sharedZoom.setZoom} labels={zoomLabels} />
                    </div>
                    {banner ? <div className="mt-2">{banner}</div> : null}
                    {picker}
                    {/* Full rose first (same as desktop), then a one-line summary, then the timeline — stacked: the phone is too narrow for two columns. */}
                    {sectionLabel(t('Ruža vetrova · poslednji sat'))}
                    <WindRoseChart cells={rose.cells} total={rose.total} binLabel={binLabel} height={340} compactLegend
                                   tooltipText={roseTooltip} emptyMessage={t('Nema očitavanja u ovom periodu')} />
                    <div className="mt-1 flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
                        <div className="text-lg font-bold text-zinc-800 dark:text-white">
                            {dominant.length === 0 ? '—' : dominant.map((d, i) => (
                                <React.Fragment key={d.direction}>
                                    {i > 0 ? <span className="mx-1 text-slate-400">·</span> : null}
                                    {d.direction} <span className="text-sm font-medium text-zinc-500 dark:text-slate-400">{Math.round(d.percentage)} %</span>
                                </React.Fragment>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-600 dark:text-slate-300">
                            {bins.map((b) => (
                                <span key={b.bin} className="inline-flex items-center gap-1.5">
                                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: ROSE_COLORS[b.index] }} />
                                    <span className="font-semibold text-zinc-800 dark:text-white">{b.bin} m/s</span>
                                    <span className="text-zinc-500 dark:text-slate-400">{Math.round(b.percentage)} %</span>
                                </span>
                            ))}
                        </div>
                    </div>
                    {sectionLabel(t('Vetar'))}
                    {/* No navigator on the phone: pinch (two fingers) zooms/pans and is mirrored across the three charts. */}
                    <WindTimelineChart points={points} sectors={sectors} height={280} labels={windLabels} {...common}
                                       shared={{ chartRef: sharedZoom.chartRefs[0], xAxisEvents: sharedZoom.xAxisEvents, pinch: true }} />
                    {hasTemp || state.loading ? (
                        <>
                            {sectionLabel(t('Temperatura, tačka rosišta i baza oblaka'))}
                            <TempDewPointChart points={points} height={220} labels={tempLabels} {...common}
                                               shared={{ chartRef: sharedZoom.chartRefs[1], xAxisEvents: sharedZoom.xAxisEvents, pinch: true }} />
                        </>
                    ) : null}
                    {hasPressure || state.loading ? (
                        <>
                            {sectionLabel(t('Vazdušni pritisak'))}
                            <PressureChart points={points} height={170} labels={pressureLabels} {...common}
                                           shared={{ chartRef: sharedZoom.chartRefs[2], xAxisEvents: sharedZoom.xAxisEvents, pinch: true }} />
                        </>
                    ) : null}
                </Box>
            </div>
        );
    }

    // ----------------------------------------------------------------- desktop
    return (
        <div className="p-2">
            {/* Section header: the range applies to ALL charts below, so it lives above them, not in a card. */}
            <div className="mx-0 sm:mx-2 mt-2 mb-1 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-zinc-700 dark:text-white text-2xl xl:text-xl font-bold tracking-wide uppercase">{t('Istorija')}</h2>
                    {banner || <p className="text-zinc-500 dark:text-slate-400 text-base font-medium tracking-tight">{subtitleWithArchive}</p>}
                </div>
                <div className="relative">{rangeToggle}{picker}</div>
            </div>
            <div className="flex flex-wrap">
                <Box sizes="w-full" uppercase title={t('Vetar')} subtitle={t('Ruža vetrova prikazuje poslednji sat')} headerRight={<div ref={setWindZoomEl} className="flex items-center" />}>
                    <div className="flex flex-col xl:flex-row xl:items-stretch gap-4 xl:gap-6">
                        <div className="flex flex-col items-center justify-center xl:w-[380px] xl:shrink-0 xl:border-r xl:border-slate-200 xl:dark:border-slate-700 xl:pr-6">
                            <WindRoseChart cells={rose.cells} total={rose.total} binLabel={binLabel} height={390} compactLegend
                                           tooltipText={roseTooltip} emptyMessage={t('Nema očitavanja u ovom periodu')} />
                        </div>
                        <div className="flex-1 min-w-0">
                            {/* Full column height (= rose height): the zoom control lives in the card header. */}
                            <WindTimelineChart points={points} sectors={sectors} height={390} labels={windLabels} zoomTarget={windZoomEl} {...common} />
                        </div>
                    </div>
                </Box>
                {hasTemp || state.loading ? (
                    <Box sizes="w-full" uppercase title={t('Temperatura, tačka rosišta i baza oblaka')} headerRight={<div ref={setTempZoomEl} className="flex items-center" />}>
                        <TempDewPointChart points={points} height={300} labels={tempLabels} zoomTarget={tempZoomEl} {...common} />
                    </Box>
                ) : null}
                {hasPressure || state.loading ? (
                    <Box sizes="w-full" uppercase title={t('Vazdušni pritisak')} headerRight={<div ref={setPressureZoomEl} className="flex items-center" />}>
                        <PressureChart points={points} height={260} labels={pressureLabels} zoomTarget={pressureZoomEl} {...common} />
                    </Box>
                ) : null}
            </div>
        </div>
    );
};

export default ChartsSection;
